import { observer } from 'mobx-react-lite';
import Highcharts, { SeriesOptionsType } from 'highcharts';
import Chart from '@components/core/chart/chart.tsx';
import { useCallback, useEffect, useState } from 'react';
import experienceStatsStore, {
	isValidGranularity,
} from '@store/experience-stats-store.ts';
import LoadingIndicator from '@components/core/loading-indicator/loading-indicator.tsx';
import { Experience } from '@/schemas/experience-schema.ts';
import hexToRGBA from '@utils/hex-to-rgba.ts';
import ChartColors from '@components/core/chart/chart-colors.ts';
import GranularitySelector from '@components/data/granularity-selector/granularity-selector.tsx';
import hashQueryParamService from '@services/hash-query-param-service.ts';
import getGraphId from '@utils/get-graph-id.ts';
import ClassString from '@utils/class-string.ts';
import { DataGranularity } from '@/enums/data-granularity.ts';
import formatSecondsDurationString from '@utils/format-seconds-duration-string.ts';
import { ExperienceStatType } from '@/enums/experience-stat-type.ts';
import ExperienceStatsAnnotations from '@components/data/experience-stats/experience-stats-annotations.tsx';
import ExperienceStatsMissingData from '@components/data/experience-stats/experience-stats-missing-data.ts';
import { fillMissingTimestamps } from '@utils/chart-data.ts';
import AnnotationSelector from '@components/data/annotation-selector/annotation-selector.tsx';

interface ExperienceStatsProps {
	experiences: Experience[];
	statType: ExperienceStatType;
	title: string;
	options?: {
		availableGranularities?: DataGranularity[];
		defaultGranularity?: DataGranularity;
		yPercentage?: boolean;
		fill?: boolean;
		showLegend?: boolean;
		thickerLines?: boolean;
		lineColor?: string;
		useLineGraph?: boolean;
		selectedRange?: number;
		info?: string;
		sharedTooltip?: boolean;
	};
}

interface ExperienceData {
	experienceId: number;
	experienceName: string;
	data: number[][];
}

const numberFormatter = new Intl.NumberFormat(navigator.language);

const getDefaultGranularity = (props: ExperienceStatsProps) => {
	if (
		props.options?.availableGranularities &&
		props.options.availableGranularities.length === 1
	) {
		return props.options.availableGranularities[0];
	}

	const hashParams = hashQueryParamService.get();
	if (
		hashParams.granularity &&
		hashParams.anchor === getGraphId(props.title) &&
		isValidGranularity(hashParams.granularity)
	) {
		return hashParams.granularity as DataGranularity;
	}

	if (
		props.options?.availableGranularities?.length &&
		props.options?.defaultGranularity &&
		!props.options.availableGranularities.includes(
			props.options.defaultGranularity
		)
	) {
		return props.options.availableGranularities[0];
	}

	if (props.options?.defaultGranularity) {
		return props.options.defaultGranularity;
	}

	return DataGranularity.DAY;
};

const ExperienceStats = observer(function ExperienceStats(
	props: ExperienceStatsProps
) {
	const [granularity, setGranularity] = useState(
		getDefaultGranularity(props)
	);
	const [loading, setLoading] = useState(true);
	const [initialized, setInitialized] = useState(false);

	const mappedData = props.experiences.map((experience) => {
		const loadedData = experienceStatsStore.experienceStat(
			experience.place_id,
			props.statType,
			granularity
		);

		return {
			experienceId: experience.place_id,
			experienceName: experience.name,
			data:
				props.experiences.length === 1 && loadedData
					? fillMissingTimestamps(loadedData, granularity)
					: loadedData,
		};
	});
	const data = mappedData.filter(
		(loadedData) => !!loadedData.data
	) as ExperienceData[];

	const noData =
		!loading &&
		(!data.length ||
			data.every((experienceData) => !experienceData.data.length));

	const getData = useCallback(
		async (tf: DataGranularity) => {
			await experienceStatsStore.getExperienceStats(
				props.experiences.map((experience) => experience.place_id),
				props.statType,
				tf
			);
			setLoading(false);
		},
		[props.experiences, props.statType]
	);

	let yMin = undefined;
	let yMax = undefined;

	if (props.statType === ExperienceStatType.RATING) {
		for (const innerData of data) {
			const rawData = innerData.data
				.map((data) => data[1])
				.sort((a, b) => a - b);

			const min = Math.max(rawData[0] - 0.5, 0);
			if (!yMin || min < yMin) {
				yMin = min;
			}

			const max = Math.min(rawData.reverse()[0] + 0.5, 100);
			if (!yMax || max > yMax) {
				yMax = max;
			}
		}
	}

	let showColumnLabels = true;
	let series: Highcharts.SeriesOptionsType[] = [];
	if (props.statType === ExperienceStatType.LIKES) {
		if (data[0]?.data) {
			showColumnLabels = data[0].data.length < 2000;
			const likesData: { likes: number[][]; dislikes: number[][] } = {
				likes: [],
				dislikes: [],
			};
			data[0].data.forEach((experienceData) => {
				likesData.likes.push([experienceData[0], experienceData[1]]);
				likesData.dislikes.push([experienceData[0], experienceData[2]]);
			});

			series = [
				{
					name: 'Likes',
					type: 'column',
					data: likesData.likes,
					showInLegend: !!props.options?.showLegend,
					color: ChartColors()[2],
				},
				{
					name: 'Dislikes',
					type: 'column',
					data: likesData.dislikes,
					showInLegend: !!props.options?.showLegend,
					color: ChartColors()[1],
				},
			];
		}
	} else {
		series = data.map((experienceData) => {
			return {
				name: experienceData.experienceName,
				type: props.options?.useLineGraph ? 'line' : 'area',
				showInLegend: !!props.options?.showLegend,
				data: experienceData.data ?? [],
				marker: {
					enabled: false,
				},
				fillOpacity: props.options?.fill ? 0.6 : 0,
				lineWidth: props.options?.thickerLines ? 3 : 2,
				lineColor: props.options?.lineColor,
				fillColor:
					props.options?.lineColor && props.options?.fill
						? hexToRGBA(props.options.lineColor, 0.6)
						: undefined,
			};
		});
	}

	let yFormatter;
	if (props.options?.yPercentage) {
		yFormatter = (
			formatter: Highcharts.AxisLabelsFormatterContextObject
		) => {
			return `${formatter.value}%`;
		};
	}

	let xMin = 0;
	let xMax = 0;
	data.forEach((pd) => {
		pd.data.forEach((d) => {
			const date = d[0];

			if (xMin === 0) {
				xMin = date;
			}

			if (xMax === 0) {
				xMax = date;
			}

			if (date < xMin) {
				xMin = date;
			}

			if (date > xMax) {
				xMax = date;
			}
		});
	});

	let missingDataSeries: {
		name: string;
		color: string;
		data: (number | null)[][] | undefined;
	}[] = [];

	if (props.experiences.length === 1 && data.length) {
		const d = data[0].data;
		missingDataSeries = ExperienceStatsMissingData({
			data: d,
			granularity,
			experienceId: props.experiences[0].place_id,
			statType: props.statType,
			color: props.options?.lineColor ?? ChartColors()[0],
			name: props.title,
		});

		if (props.statType !== ExperienceStatType.LIKES) {
			series = [
				...series,
				...missingDataSeries.map((series): SeriesOptionsType => {
					return {
						name: series.name,
						type: 'line',
						showInLegend: false,
						data: series.data ?? [],
						marker: {
							enabled: false,
						},
						lineWidth: 3,
						color: series.color,
						dashStyle: 'Dash',
						enableMouseTracking: false,
					};
				}),
			];
		}
	}

	const options: Highcharts.Options = {
		chart: {
			marginLeft: 45,
		},
		series,
		xAxis: {
			type: 'datetime',
			min: xMin,
			max: xMax,
		},
		yAxis: {
			title: {
				enabled: false,
			} as Highcharts.YAxisOptions,
			labels: {
				x: props.statType === ExperienceStatType.RATING ? -5 : -10,
				y: 3,
				reserveSpace: false,
				style: {
					whiteSpace: 'nowrap',
				},
				formatter: yFormatter,
			},
			min: yMin,
			max: yMax,
		},
		plotOptions: {
			line: {
				marker: {
					enabled: false,
				},
			},
			column: {
				stacking: 'normal',
				dataLabels: {
					enabled: showColumnLabels,
				},
			},
		},
		navigator: {
			enabled: props.statType !== ExperienceStatType.RATING,
			series:
				props.statType === ExperienceStatType.LIKES
					? data.map((pd) => {
							return {
								data: pd.data.map((experienceData) => [
									experienceData[0],
									experienceData[2],
								]),
								color: ChartColors()[1],
							};
						})
					: [
							...(data.map((pd) => {
								return {
									data: pd.data,
									color: props.options?.lineColor,
								};
							}) as SeriesOptionsType[]),
							...missingDataSeries.map(
								(series): SeriesOptionsType => {
									return {
										name: series.name,
										type: 'line',
										showInLegend: false,
										data: series.data ?? [],
										marker: {
											enabled: false,
										},
										lineWidth: 1,
										color: series.color,
										dashStyle: 'Dash',
										enableMouseTracking: false,
									};
								}
							),
						],
			xAxis: {
				min: xMin,
				max: xMax,
			},
		},
		scrollbar: {
			enabled: props.statType !== ExperienceStatType.RATING,
			liveRedraw: true,
		},
		rangeSelector: {
			enabled: props.statType !== ExperienceStatType.RATING,
			selected: props.options?.selectedRange ?? 2,
		},
		tooltip: {
			pointFormatter: function () {
				// Handle line series (single y value)
				if (this.y === undefined) {
					return '';
				}
				const value = this.y;
				const color = this.color as string;
				if (props.statType === ExperienceStatType.RATING) {
					return `<span style="color:${color}">●</span> ${this.series.name}: <b>${Number(value.toFixed(2))}%</b><br/>`;
				} else if (props.statType === ExperienceStatType.PLAYTIME) {
					const formattedTime = formatSecondsDurationString(value);
					return `<span style="color:${color}">●</span> ${this.series.name}: <b>${formattedTime}</b><br/>`;
				} else {
					return `<span style="color:${color}">●</span> ${this.series.name}: <b>${numberFormatter.format(value)}</b><br/>`;
				}
			},
			...(props.options?.sharedTooltip
				? { shared: true, split: false, distance: 10, snap: 8 }
				: {}),
		},
		annotations:
			props.experiences.length === 1 && data[0]?.data
				? ExperienceStatsAnnotations({
						experience: props.experiences[0],
						data: data[0].data,
						options: {
							yMinInside:
								props.statType === ExperienceStatType.RATING,
						},
					})
				: undefined,
	};

	const updateGranularity = (g: DataGranularity) => {
		void getData(g);
		setGranularity(g);
		setLoading(true);
	};

	useEffect(() => {
		if (!initialized && !loading) {
			setInitialized(true);
		}
	}, [initialized, loading]);

	useEffect(() => {
		void getData(granularity);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.experiences]);

	return (
		<div>
			<div className={'relative'}>
				<div
					className={ClassString({
						static: 'transition-opacity',
						dynamic: { 'opacity-0': loading && !initialized },
					})}
				>
					<Chart
						title={props.title}
						highcharts={Highcharts}
						options={options}
						granularity={granularity}
						info={props.options?.info}
						fillPanelResponsiveWidth
					/>
				</div>

				{loading && (
					<div
						className={
							'absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2'
						}
					>
						<LoadingIndicator />
					</div>
				)}
				{noData && (
					<div
						className={
							'absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2'
						}
					>
						No Data
					</div>
				)}
			</div>

			<div className={'w-full flex justify-between items-center -mt-4'}>
				<GranularitySelector
					updateGranularity={updateGranularity}
					selectedGranularity={granularity}
					availableGranularities={
						props.options?.availableGranularities
					}
				/>

				<AnnotationSelector
					experience={props.experiences[0]}
					className={ClassString({
						static: 'hidden sm:flex h-full',
						dynamic: {
							'sm:hidden': props.experiences.length !== 1,
						},
					})}
				/>
			</div>
		</div>
	);
});

export default ExperienceStats;
